import 'core-js/modules/web.dom-collections.iterator.js';
import * as React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { useField } from 'formik';
import { selectFromTheme } from '@thisisbud/sc-utils';
import { inputLabelClassName, inputHintClassName, inputErrorClassName } from '@thisisbud/bui-react-typography';
import { DownChevronIcon } from '@thisisbud/bui-react-icons';
import { jsxs, jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t,
  _DownChevronIcon;
const selectInputClassName = 'bui-select-input';
const selectInputIconClassName = `${selectInputClassName}__icon`;
const selectInputErrorStateClassName = `${selectInputClassName}--error`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledWrapper",
  componentId: "sc-1o73udd-0"
})(_t || (_t = _`
  position: relative;

  .${0} + &,
  .${0} + &,
  .${0} + & {
    margin-top: ${0};
  }

  .${0} {
    position: absolute;
    display: block;
    right: ${0};
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 2;
    font-size: ${0};
    color: ${0};
  }

  select {
    position: relative;
    display: block;
    width: 100%;
    padding: ${0};
    padding-right: ${0};
    background-color: ${0};
    text-overflow: ellipsis;
    overflow: hidden;
    box-sizing: border-box;
    opacity: 1;
    appearance: none;
    z-index: 1;
    transition: border-color 0.2s ease-in-out, outline 0.2s ease-in-out;
    border-style: solid;
    border-radius: ${0};
    border-width: ${0};
    border-color: ${0};
    outline: ${0} solid ${0}00;
    outline-offset: ${0};
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    letter-spacing: ${0};
    line-height: ${0};
    color: ${0};

    :focus {
      outline: ${0} solid ${0};
    }
    
    :disabled {
      background-color: ${0};
      border-color: ${0};
      color: ${0};
      opacity: 1;
    }
  }

  select::-ms-expand {
    display: none;
  }

  select option {
    font-family: ${0};
    font-size: ${0};
    font-weight: ${0};
    letter-spacing: ${0};
    line-height: ${0};
  }

  select:disabled ~ .${0} {
    color: ${0};
  }

  &.${0} {
    select {
      background-color: ${0};
      border-color: ${0};
      color: ${0};

      :focus {
        outline: ${0} solid ${0};
      }
    }
  }
`), inputLabelClassName, inputHintClassName, inputErrorClassName, selectFromTheme('spacingXXXSmallDefault'), selectInputIconClassName, selectFromTheme('spacingXXSmallDefault'), selectFromTheme('fontSizeInputDefault'), selectFromTheme('colorTextPrimaryOff'), selectFromTheme('paddingInputDefault'), "3rem", selectFromTheme('colorBackgroundInputDefault'), selectFromTheme('borderRadiusInputDefault'), selectFromTheme('borderWidthInputDefault'), selectFromTheme('colorBorderInputDefault'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('colorTextPrimaryAccent'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('fontFamilyInput'), selectFromTheme('fontSizeInputDefault'), selectFromTheme('fontWeightInput'), selectFromTheme('letterSpacingInputDefault'), selectFromTheme('lineHeightInputDefault'), selectFromTheme('colorTextInputDefault'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('colorTextPrimaryAccent'), selectFromTheme('colorBackgroundInputDisabled'), selectFromTheme('colorBorderInputDisabled'), selectFromTheme('colorTextInputDisabled'), selectFromTheme('fontFamilyInput'), selectFromTheme('fontSizeInputDefault'), selectFromTheme('fontWeightInput'), selectFromTheme('letterSpacingInputDefault'), selectFromTheme('lineHeightInputDefault'), selectInputIconClassName, selectFromTheme('colorTextPrimaryFaded'), selectInputErrorStateClassName, selectFromTheme('colorBackgroundInputError'), selectFromTheme('colorBorderInputError'), selectFromTheme('colorTextInputError'), selectFromTheme('outlineWidthInputDefault'), selectFromTheme('colorBorderInputError'));

/**
 * Renders a select input field for pre-defined input options.
 *
 * *The `SelectInput` component needs to be a descendent of a `Form` component and its name must match one of its configured fields.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { SelectInput } from '@thisisbud/bui-react-select-input'
 *
 * function Example() {
 *   return (
 *     <SelectInput id='example' name='example'>
 *       <option value='foo'>Foo</option>
 *       <option value='bar'>Bar</option>
 *     </SelectInput>
 *   )
 * }
 * ```
 */
function SelectInput(props) {
  // eslint-disable-line max-lines-per-function
  const {
    autoComplete,
    children,
    className,
    describedBy,
    disabled,
    error,
    id,
    name,
    onBlur,
    onChange,
    required
  } = props;
  const [field, meta, helpers] = useField({
    name
  });
  const computedError = Boolean(error !== null && error !== void 0 ? error : meta.error) && !disabled; // eslint-disable-line @typescript-eslint/strict-boolean-expressions

  // Allow using using onBlur event listeners
  const onBlurHandler = React.useCallback(function (e) {
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    field.onBlur(e);
  }, [field, onBlur]);

  // Allow using using onChange event listeners
  const onChangeHandler = React.useCallback(function (e) {
    if (typeof onChange === 'function') {
      onChange(e);
    }

    // Prevent value changes if the default is prevented
    if (!e.defaultPrevented) {
      if (typeof meta.error === 'string') {
        helpers.setError(undefined);
      }
      field.onChange(e);
    }
  }, [field, helpers, meta, onChange]);
  return /*#__PURE__*/jsxs(StyledWrapper, {
    className: classnames(selectInputClassName, {
      [selectInputErrorStateClassName]: computedError
    }, className),
    children: [/*#__PURE__*/jsx("select", {
      "aria-describedby": describedBy,
      autoComplete: autoComplete,
      disabled: disabled,
      id: id,
      name: name,
      required: required,
      value: field.value,
      onBlur: onBlurHandler,
      onChange: onChangeHandler,
      children: children
    }), _DownChevronIcon || (_DownChevronIcon = /*#__PURE__*/jsx(DownChevronIcon, {
      className: selectInputIconClassName
    }))]
  });
}
SelectInput.displayName = 'SelectInput';

export { SelectInput, selectInputClassName };
